import * as THREE from "three";
import type { Partners } from "../partners.ts";
import { createSprite } from "./sprite.ts";
import { flatWhite } from "./materials.ts";

// Partner tables scattered across the open floor (not tied to walls). Order
// matches assets/partners.json slugs; positions are hand-placed so they're
// spread around the room and clear of the entrance (player spawns at z≈5).
export const PARTNER_PLACES: { slug: string; x: number; z: number }[] = [
  // Hidden for now — re-enable to bring these booths back.
  // { slug: "el-espectador", x: -14, z: 9 },
  // { slug: "la-silla-vacia", x: 15, z: -11 },
  { slug: "economia-para-la-pipol", x: -3, z: -18 },
];

export interface PartnersRig {
  group: THREE.Group;
  // Call each frame: bob/jump the logos and keep them facing the camera.
  update(dt: number, camera: THREE.Camera): void;
}

const LOGO_Y = 2.45; // hover height above the table
const HOP = 0.38; // jump amplitude

// A little line-art table: a top on four legs (black edges added by build.ts).
function buildTable(): THREE.Group {
  const t = new THREE.Group();
  const top = new THREE.Mesh(new THREE.BoxGeometry(2.8, 0.2, 1.4), flatWhite());
  top.position.y = 1.05;
  t.add(top);
  for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    const leg = new THREE.Mesh(
      new THREE.BoxGeometry(0.16, 1.05, 0.16),
      flatWhite(),
    );
    leg.position.set(sx * 1.2, 0.52, sz * 0.55);
    t.add(leg);
  }
  return t;
}

interface FloatLogo {
  mesh: THREE.Object3D;
  phase: number;
  speed: number;
}

// Partner booths: tables scattered on the floor, each with a bouncing,
// camera-facing logo above. Walking up opens their tools modal.
export function createPartners(data: Partners): PartnersRig {
  const group = new THREE.Group();
  group.name = "partners";
  const logos: FloatLogo[] = [];
  let elapsed = 0;

  for (const place of PARTNER_PLACES) {
    const p = data.partners.find((x) => x.slug === place.slug);
    if (!p) continue;

    const table = buildTable();
    table.position.set(place.x, 0, place.z);
    table.rotation.y = Math.random() * Math.PI; // a little variety
    group.add(table);

    const logo = createSprite(p.logo, 1.1);
    logo.mesh.position.set(place.x, LOGO_Y, place.z);
    group.add(logo.mesh);
    logos.push({
      mesh: logo.mesh,
      phase: Math.random() * Math.PI * 2,
      speed: 2.4 + Math.random() * 0.6,
    });
  }

  return {
    group,
    update(dt, camera) {
      elapsed += dt;
      for (const l of logos) {
        l.mesh.position.y = LOGO_Y +
          Math.abs(Math.sin(elapsed * l.speed + l.phase)) * HOP;
        l.mesh.rotation.y = Math.atan2(
          camera.position.x - l.mesh.position.x,
          camera.position.z - l.mesh.position.z,
        );
      }
    },
  };
}
